// Shared filter vocabulary for the facility-backed calendars (Facilities Matrix
// and the single-facility calendar). Keeping the statuses, swatches and
// operating-hours maths in one place is what stops the two views drifting into
// separate legends for the same colours.
import React from "react";
import { FormControl, Checkbox, CheckboxGroup } from "@primer/react";

// The legend arrives from the server (EventRequest.status_legend) as
// [{ value, label, variant }], so it lists every status in the grids' colours.
export function requestSwatch(variant) {
  return { borderColor: `var(--label-${variant}-fgColor-rest)` };
}

export function statusValues(statuses) {
  return statuses.map((s) => s.value);
}

export function Swatch({ style }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        flexShrink: 0,
        border: "2px dashed",
        backgroundColor: "transparent",
        ...style,
      }}
    />
  );
}

export function StatusCheckboxes({ statuses, selected, onChange }) {
  const toggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  return (
    <CheckboxGroup>
      <CheckboxGroup.Label>Request Status</CheckboxGroup.Label>
      {statuses.map((s) => (
        <FormControl key={s.value}>
          <Checkbox
            checked={selected.includes(s.value)}
            onChange={() => toggle(s.value)}
          />
          <FormControl.LeadingVisual>
            <Swatch style={requestSwatch(s.variant)} />
          </FormControl.LeadingVisual>
          <FormControl.Label>{s.label}</FormControl.Label>
        </FormControl>
      ))}
    </CheckboxGroup>
  );
}

export function filterEventsByStatus(events, selected, statuses) {
  if (selected.length >= statuses.length) return events;

  // GR-4399: a status the legend does not list (cancelled) is not the
  // legend's to hide; those bookings arrive only when "Show Archived /
  // Cancelled" asked the server for them.
  const listed = statusValues(statuses);
  return events.filter((e) => {
    const status = e.extendedProps?.requestStatus;
    return !status || !listed.includes(status) || selected.includes(status);
  });
}

const DOW_MAP = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
const ALL_DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function parseTime(str) {
  const [h, m] = str.split(":").map(Number);
  return h * 60 + (m || 0);
}

function minutesToTime(mins) {
  const h = String(Math.floor(mins / 60)).padStart(2, "0");
  const m = String(mins % 60).padStart(2, "0");
  return `${h}:${m}:00`;
}

export function computeOperatingHours(facilities) {
  let earliestOpen = 24 * 60;
  let latestClose = 0;
  const dayHours = {};

  for (const f of facilities) {
    const hours = f.operatingHours;
    if (!hours || Object.keys(hours).length === 0) continue;

    for (const [day, range] of Object.entries(hours)) {
      if (!range || !range.includes("-")) continue;
      const [openStr, closeStr] = range.split("-");
      const open = parseTime(openStr);
      const close = parseTime(closeStr);

      if (open < earliestOpen) earliestOpen = open;
      if (close > latestClose) latestClose = close;

      // Track earliest/latest per day for business hours (use most restrictive = latest open, earliest close)
      if (!dayHours[day]) dayHours[day] = { open: 0, close: 24 * 60, count: 0 };
      dayHours[day].count++;
      if (open > dayHours[day].open) dayHours[day].open = open;
      if (close < dayHours[day].close) dayHours[day].close = close;
    }
  }

  // Default to 8am-6pm if no operating hours defined
  if (earliestOpen >= latestClose) {
    earliestOpen = 8 * 60;
    latestClose = 18 * 60;
  }

  // Build FullCalendar businessHours entries per day
  const businessHours = ALL_DAYS
    .filter((day) => dayHours[day])
    .map((day) => ({
      daysOfWeek: [DOW_MAP[day]],
      startTime: minutesToTime(dayHours[day].open),
      endTime: minutesToTime(dayHours[day].close),
    }));

  return {
    earliestOpen: minutesToTime(earliestOpen),
    latestClose: minutesToTime(latestClose),
    businessHours: businessHours.length > 0 ? businessHours : undefined,
  };
}
